import * as React from 'react'
import { cn } from '../../lib/utils'

/**
 * ErrorPage — a full-surface error / not-found page: an oversized status code,
 * a title, a description, and an action row (drop one or two <Button>s in), over
 * a faint decorative line-grid that fades into each corner.
 *
 * This is the canonical home for 404 / 403 / 500 / generic "something went
 * wrong" pages (the app hand-rolls these as one-off NotFound / NotAuthorized
 * cards today). For a compact in-content "no data" placeholder use <EmptyState>;
 * for an inline colored status notice use <Alert>.
 *
 * The component fills its container (min-height floor so it looks right
 * standalone). Route it into your page's content region, or pass
 * `className="min-h-screen"` for a true full-viewport error page.
 */

export interface ErrorPageProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Oversized status code shown above the title, e.g. `404`. Decorative. */
  code?: React.ReactNode
  /** The primary error message. */
  title: React.ReactNode
  /** Optional supporting copy beneath the title. */
  description?: React.ReactNode
  /** Optional action slot — typically one or two <Button>s. */
  action?: React.ReactNode
  /** Render the faint corner line-grid backdrop (default true). */
  decoration?: boolean
  /**
   * Heading level for the title (default 1). Drop to 2+ when the error renders
   * inside a page that already owns the `<h1>`, to keep the outline valid.
   */
  headingLevel?: 1 | 2 | 3 | 4 | 5 | 6
}

export const ErrorPage = React.forwardRef<HTMLDivElement, ErrorPageProps>(
  (
    {
      className,
      code,
      title,
      description,
      action,
      decoration = true,
      headingLevel = 1,
      ...props
    },
    ref
  ) => {
    const Heading = `h${headingLevel}` as const
    return (
    <div
      ref={ref}
      data-slot="error-page"
      className={cn(
        'relative flex min-h-[32rem] w-full items-center justify-center overflow-hidden bg-fbui-background px-4',
        className
      )}
      {...props}
    >
      {decoration && <ErrorPageDecoration />}
      <div className="relative z-10 mx-auto max-w-xl text-center sm:px-5">
        {code != null && (
          <p
            aria-hidden="true"
            data-slot="error-page-code"
            className="text-center text-[72px] font-medium leading-none text-fbui-gray-900 lg:text-[140px]"
          >
            {code}
          </p>
        )}
        <Heading
          data-slot="error-page-title"
          className="mb-3 text-center text-3xl font-semibold tracking-[-0.4px] text-fbui-gray-900 lg:text-4xl"
        >
          {title}
        </Heading>
        {description != null && (
          <p
            data-slot="error-page-description"
            className="mb-4 text-fbui-gray-500"
          >
            {description}
          </p>
        )}
        {action && (
          <div
            data-slot="error-page-action"
            className="mx-auto mt-9 flex flex-col justify-center gap-3 sm:flex-row sm:items-center"
          >
            {action}
          </div>
        )}
      </div>
    </div>
    )
  }
)
ErrorPage.displayName = 'ErrorPage'

/**
 * The decorative backdrop: a faint line-grid in the top-right and bottom-left
 * corners, each fading out along a diagonal. Colored via `currentColor`
 * (fbui-gray-200) and hidden from assistive tech. Gradient ids are scoped with
 * useId so multiple instances never collide.
 */
function ErrorPageDecoration() {
  const gid = React.useId()
  const trGrad = `${gid}-tr`
  const blGrad = `${gid}-bl`
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 text-fbui-gray-200"
    >
      {/* top-right */}
      <svg
        className="absolute right-0 top-0 block"
        width="450"
        height="254"
        viewBox="0 0 450 254"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g fill={`url(#${trGrad})`}>
          <path d="M0.505572 45.1131L450 45.1132L450 44.6073L0.505572 44.6072L0.505572 45.1131Z" />
          <path d="M205.546 253.529L205.546 -2.13709e-05L205.04 -2.1392e-05L205.04 253.529L205.546 253.529Z" />
          <path d="M0.505568 97.2164L450 97.2165L450 96.7106L0.505568 96.7106L0.505568 97.2164Z" />
          <path d="M256.806 253.529L256.806 -1.68895e-05L256.3 -1.69106e-05L256.3 253.529L256.806 253.529Z" />
          <path d="M0.505859 253.529L0.505882 -3.9296e-05L2.21642e-05 -3.93171e-05L0 253.529L0.505859 253.529Z" />
          <path d="M0.505563 149.321L450 149.321L450 148.815L0.505563 148.815L0.505563 149.321Z" />
          <path d="M308.066 253.529L308.066 -1.24083e-05L307.56 -1.24294e-05L307.56 253.529L308.066 253.529Z" />
          <path d="M51.7662 253.529L51.7662 -3.48147e-05L51.2604 -3.48358e-05L51.2603 253.529L51.7662 253.529Z" />
          <path d="M0.505559 201.424L450 201.424L450 200.918L0.505559 200.918L0.505559 201.424Z" />
          <path d="M359.326 253.529L359.326 -7.92695e-06L358.82 -7.94806e-06L358.82 253.529L359.326 253.529Z" />
          <path d="M103.026 253.529L103.026 -3.03334e-05L102.52 -3.03545e-05L102.52 253.529L103.026 253.529Z" />
          <path d="M410.586 253.529L410.586 -3.44569e-06L410.08 -3.4668e-06L410.08 253.529L410.586 253.529Z" />
          <path d="M154.286 253.529L154.286 -2.58521e-05L153.78 -2.58732e-05L153.78 253.529L154.286 253.529Z" />
        </g>
        <rect
          opacity="0.3"
          width="50.7536"
          height="51.5982"
          transform="matrix(-1 -8.74228e-08 -8.74228e-08 1 358.82 45.1138)"
          fill="currentColor"
        />
        <rect
          opacity="0.3"
          width="50.756"
          height="51.5985"
          transform="matrix(-1 -8.74228e-08 -8.74228e-08 1 307.559 97.2161)"
          fill="currentColor"
        />
        <defs>
          <linearGradient
            id={trGrad}
            x1="277.872"
            y1="-9.94587e-06"
            x2="194.87"
            y2="235.867"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="currentColor" />
            <stop offset="1" stopColor="currentColor" stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>

      {/* bottom-left */}
      <svg
        className="absolute bottom-0 left-0 block"
        width="450"
        height="254"
        viewBox="0 0 450 254"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g fill={`url(#${blGrad})`}>
          <path d="M449.494 208.416L-7.88783e-06 208.416L-7.79939e-06 208.922L449.494 208.921L449.494 208.416Z" />
          <path d="M244.454 7.93286e-05L244.454 253.529L244.96 253.529L244.96 7.92401e-05L244.454 7.93286e-05Z" />
          <path d="M449.494 156.312L-1.69979e-05 156.312L-1.69094e-05 156.818L449.494 156.818L449.494 156.312Z" />
          <path d="M193.194 8.82912e-05L193.194 253.529L193.7 253.529L193.7 8.82028e-05L193.194 8.82912e-05Z" />
          <path d="M449.494 4.34783e-05L449.494 253.529L450 253.529L450 4.33898e-05L449.494 4.34783e-05Z" />
          <path d="M449.494 104.208L-2.61081e-05 104.208L-2.60197e-05 104.714L449.494 104.714L449.494 104.208Z" />
          <path d="M141.934 9.72538e-05L141.934 253.529L142.44 253.529L142.44 9.71653e-05L141.934 9.72538e-05Z" />
          <path d="M398.234 5.24409e-05L398.234 253.529L398.74 253.529L398.74 5.23525e-05L398.234 5.24409e-05Z" />
          <path d="M449.494 52.1045L-3.52181e-05 52.1046L-3.51297e-05 52.6104L449.494 52.6104L449.494 52.1045Z" />
          <path d="M90.6737 0.000106216L90.6738 253.529L91.1796 253.529L91.1796 0.000106128L90.6737 0.000106216Z" />
          <path d="M346.974 6.14034e-05L346.974 253.529L347.48 253.529L347.48 6.1315e-05L346.974 6.14034e-05Z" />
          <path d="M39.4141 0.000115179L39.4141 253.529L39.92 253.529L39.9199 0.00011509L39.4141 0.000115179Z" />
          <path d="M295.714 7.03661e-05L295.714 253.529L296.22 253.529L296.22 7.02776e-05L295.714 7.03661e-05Z" />
        </g>
        <rect
          opacity="0.3"
          width="50.7536"
          height="51.5982"
          transform="matrix(1 -1.74846e-07 -1.74846e-07 -1 91.1797 208.415)"
          fill="currentColor"
        />
        <rect
          opacity="0.3"
          width="50.756"
          height="51.5985"
          transform="matrix(1 -1.74846e-07 -1.74846e-07 -1 142.441 156.313)"
          fill="currentColor"
        />
        <defs>
          <linearGradient
            id={blGrad}
            x1="172.128"
            y1="253.529"
            x2="255.13"
            y2="17.6622"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="currentColor" />
            <stop offset="1" stopColor="currentColor" stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  )
}
